import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import LoaderInner from './LoaderInner';
import { getCookie } from '../jwt/_helpers';

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const PaymentFailed = ({ isPaymentFailedModalOpen, setIsPaymentFailedModalOpen, currentUser }) => {
    const token = getCookie("token");
    const options = useMemo(() => ({
      method: "POST",
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    }), [token]);

    const [paymentUpdateLink, setPaymentUpdateLink] = useState(null);
    const [failedPaymentDetails, setFailedPaymentDetails] = useState(null);
    const [newPaymentMethod, setNewPaymentMethod] = useState(null);
    const [paymentRetryInProgress, setPaymentRetryInProgress] = useState(false);

    const getPaymentUpdateLink = useCallback(async () => {
        const response = await fetch(`${API_URL}wl-update-payment-method/`, options);
        const data = await response.json();
        let updateLink = data.url + "/payment-methods/";
        return updateLink;
    }, [options]);

    const retryPayment = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const response = await fetch(`${API_URL}wl-retry-payment/`, options);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.status === 1) {
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error in retryPayment:', error);
            throw error;
        }
    }

    useEffect(() => {
        if (!isPaymentFailedModalOpen || paymentUpdateLink) {
            return;
        }
        getPaymentUpdateLink().then(url => {
            setPaymentUpdateLink(url);
        });
    }, [getPaymentUpdateLink, isPaymentFailedModalOpen, paymentUpdateLink]);

    useEffect(() => {
        if(currentUser && currentUser.wl_failed_payment_details) {
            setFailedPaymentDetails(currentUser.wl_failed_payment_details);
        }
        if(currentUser && currentUser.new_payment_method)
        {
            setNewPaymentMethod(currentUser.new_payment_method);
        }
    }, [currentUser]);

    return (
        <>
            <Modal isOpen={isPaymentFailedModalOpen} backdrop="static" keyboard={false} centered size="lg" style={{position: 'relative'}}>
                <LoaderInner show={paymentRetryInProgress} />
                <ModalHeader>Payment Failed</ModalHeader>
                <ModalBody>
                    <p>Your last payment {failedPaymentDetails && <>of <strong>{failedPaymentDetails.invoice_amount}</strong></>} has failed for the SLA Subscription on your account.</p>
                    {failedPaymentDetails && 
                        <>
                            {failedPaymentDetails.failure_reason && <p><strong>Reason</strong><br/><strong style={{color: "red"}}>{failedPaymentDetails.failure_reason}</strong></p>}
                            {
                                failedPaymentDetails?.payment_method && (
                                    <p>
                                        <strong>Failed Payment Method</strong><br/>
                                        {(() => {
                                            const paymentMethod = failedPaymentDetails.payment_method;
                                            if (paymentMethod && paymentMethod.card) {
                                                const { brand, last4 } = paymentMethod.card;
                                                return (
                                                    <div>
                                                        <i className="fas fa-md fa-credit-card mr-1"></i>
                                                        <strong className="text-capitalize">{brand} **** **** **** {last4}</strong>                                                       
                                                    </div>
                                                );
                                            }
                                        })()}
                                    </p>
                                )
                            }
                        </>
                    }
                    Please follow the steps below to have uninterrupted access to our services:
                    <br/><br/>
                    <strong>Step 1</strong>
                    <br/>
                    Please add a different <b>Payment Method</b> to your account.
                    <br/><br/>
                    <strong>Step 2</strong>
                    <br/>
                    Once you have added a different Payment Method, proceed to <b>Retry Payment</b>.
                    <br/><br/>
                    {(currentUser.payment_method_updated && newPaymentMethod) &&
                    <p>
                        <strong>Updated Payment Method</strong><br/>
                        {(() => {
                            if (newPaymentMethod && newPaymentMethod.card) {
                                const { brand, last4 } = newPaymentMethod.card;
                                return (
                                    <div>
                                        <i className="fas fa-md fa-credit-card mr-1"></i>
                                        <strong className="text-capitalize">{brand} **** **** **** {last4}</strong>                                                       
                                    </div>
                                );
                            }
                        })()}
                    </p>
                    }
                    <p><b>Note:</b> This message is only visible to you and will not be shown to your customers.</p>
                    <p>If you have any questions, please contact our support team.</p>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={() => {
                            setPaymentRetryInProgress(true);
                            retryPayment().then(res => {
                                if (res) {
                                    setIsPaymentFailedModalOpen(false);
                                    setPaymentRetryInProgress(false);
                                    window.location.href = "/sla/payment/success";
                                } else {
                                    setPaymentRetryInProgress(false);
                                    window.location.href = "/sla/payment/failure";
                                }
                            }).catch(error => {
                                setPaymentRetryInProgress(false);
                                window.location.href = "/sla/payment/failure";
                            })
                        }}
                        disabled={!currentUser.payment_method_updated || paymentRetryInProgress}
                    >
                        {paymentRetryInProgress ? 'Retrying...' : 'Retry Payment'}
                    </Button>
                    <Button
                        color="success"
                        onClick={() => window.location.href = paymentUpdateLink}
                        disabled={!paymentUpdateLink}
                    >
                        Update Payment Method
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default PaymentFailed;